// ============================================================================
// src/layer/layerRecordOps.ts — the layer pages' one runner for migration 242
// ----------------------------------------------------------------------------
// The layer batch / housing pages, the batches hub and the /admin feed-rate
// fan-out send ONE layer_apply_operation per user action (src/lib/
// layerOperationsApi.ts) instead of whole-row upserts, and no longer write
// Activity themselves: the server writes it in the same transaction.
//
// This module holds what those callers share:
//   * the allowlists and labels, mirroring 242's c_batch_fields /
//     c_housing_fields exactly (the static guard pins both sides);
//   * the save-status lifecycle the header shows ('saving' -> 'saved'/'error');
//   * a stable request id per INTENT: an autosave retry of the same change
//     reuses its id (the server replays instead of re-applying); any other
//     change takes a new id;
//   * the cache rule from 4813884f: a confirmed write marks every layer read
//     stale, so a record reopened right after a save never reseeds from a
//     pre-save cache.
// ============================================================================

import type {QueryClient} from '@tanstack/react-query';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {SaveStatusSink} from '../lib/appStoreBlobApi.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import {
  applyLayerOperation,
  newLayerRequestId,
  type LayerOpOutcome,
  type LayerOpRequest,
} from '../lib/layerOperationsApi.js';

/** 242's c_batch_fields: what the batch editor edits (id is immutable). */
export const LAYER_BATCH_FIELDS = [
  'name',
  'status',
  'arrival_date',
  'original_count',
  'supplier',
  'cost_per_bird',
  'brooder_name',
  'brooder_entry_date',
  'brooder_exit_date',
  'schooner_name',
  'schooner_entry_date',
  'schooner_exit_date',
  'notes',
  'per_lb_starter_cost',
  'per_lb_grower_cost',
  'per_lb_layer_cost',
] as const;

/** 242's c_housing_fields (batch_id immutable; current_count_date is the server's stamp). */
export const LAYER_HOUSING_FIELDS = [
  'housing_name',
  'status',
  'start_date',
  'retired_date',
  'notes',
  'current_count',
] as const;

/** The Retirement Home housing fields the combined editor edits (242's c_rh_housing_fields). */
export const RETIREMENT_HOME_HOUSING_FIELDS = ['current_count', 'start_date', 'notes'] as const;

/** The labels 242 writes into Activity, for the conflict notice. */
export const LAYER_FIELD_LABELS: Record<string, string> = {
  name: 'Batch Name',
  status: 'Status',
  arrival_date: 'Arrival Date',
  original_count: 'Original Count',
  supplier: 'Supplier',
  cost_per_bird: 'Cost per Bird',
  brooder_name: 'Brooder',
  brooder_entry_date: 'Date in Brooder',
  brooder_exit_date: 'Date out of Brooder',
  schooner_name: 'Schooner',
  schooner_entry_date: 'Date in Schooner',
  schooner_exit_date: 'Date out of Schooner',
  notes: 'Notes',
  per_lb_starter_cost: 'Starter Cost per lb',
  per_lb_grower_cost: 'Grower Cost per lb',
  per_lb_layer_cost: 'Layer Cost per lb',
  housing_name: 'Housing Name',
  start_date: 'Start Date',
  retired_date: 'Retired Date',
  current_count: 'Current Count',
  current_count_date: 'Count Date',
};

/** Every read that shows a layer batch or housing (the Retirement Home editor's list, unchanged). */
export const LAYER_READ_RESOURCES = [
  RESOURCE.LAYER_RECORD_CORE,
  RESOURCE.LAYER_BATCHES_CORE,
  RESOURCE.LAYERS_HOME_CORE,
  RESOURCE.LAYER_GROUPS_CORE,
  RESOURCE.LAYER_DAILYS_GROUP_DIRECTORY,
  RESOURCE.WEBFORM_HUB_LAYER_DIRECTORY,
  RESOURCE.ANIMAL_HISTORY_LAYER_DIRECTORY,
  RESOURCE.BROILER_FEED_CORE,
] as const;

export function invalidateLayerReads(queryClient: Pick<QueryClient, 'invalidateQueries'>): Promise<unknown> {
  return Promise.allSettled(LAYER_READ_RESOURCES.map((id) => queryClient.invalidateQueries({queryKey: [id]})));
}

/**
 * The row an editor's expected-before values come from: the server's last
 * confirmed reply for record `id` when the page holds one, else the rendered
 * row. The autosave drain persists a queued revision before React re-renders,
 * so the rendered row can still be the one the previous save replaced
 * (REVIEW-242 F1); the confirmed reply is set synchronously by the page.
 */
export function confirmedRow<T extends {id?: unknown}>(
  saved: T | null | undefined,
  id: unknown,
  rendered: T | null | undefined,
): T | null {
  return saved && saved.id === id ? saved : (rendered ?? null);
}

/** One request id per intent: the same change retried keeps its id; a new change gets a new one. */
export function createRequestIdMemo() {
  let last: {intent: string; requestId: string} | null = null;
  return {
    idFor(request: Omit<LayerOpRequest, 'requestId'>): string {
      const intent = JSON.stringify(request);
      if (last && last.intent === intent) return last.requestId;
      last = {intent, requestId: newLayerRequestId()};
      return last.requestId;
    },
    /** A confirmed (or definitively refused) outcome ends the action. */
    settle() {
      last = null;
    },
  };
}

export type LayerOpRunner = (request: Omit<LayerOpRequest, 'requestId'>) => Promise<LayerOpOutcome>;

/**
 * Apply operations in order (one chain per page), reusing the request id while
 * the same intent is retried, and keep the header's save status honest.
 */
export function createLayerOpRunner(deps: {
  sb: SupabaseClient;
  onSaveStatus: SaveStatusSink;
  queryClient?: Pick<QueryClient, 'invalidateQueries'>;
}): LayerOpRunner {
  const memo = createRequestIdMemo();
  let chain: Promise<unknown> = Promise.resolve();
  return (request) => {
    const run = chain.then(async () => {
      deps.onSaveStatus('saving');
      const outcome = await applyLayerOperation(deps.sb, {...request, requestId: memo.idFor(request)});
      // Only an unknown outcome keeps the id: the same change, retried, must replay.
      if (outcome.status !== 'unknown') memo.settle();
      const ok = outcome.status === 'saved' || outcome.status === 'no_change';
      if (outcome.status === 'saved' && deps.queryClient) void invalidateLayerReads(deps.queryClient);
      deps.onSaveStatus(ok ? 'saved' : 'error');
      setTimeout(() => deps.onSaveStatus(''), ok ? 2500 : 4000);
      return outcome;
    });
    chain = run.catch(() => undefined);
    return run;
  };
}
